import { AlertTriangle, ExternalLink } from "lucide-react";
import PropTypes from "prop-types";
import Modal from "@components/ui-components/modal";
import LoadingButton from "@components/ui-components/loading-button";
import { ComponentSpinner } from "@components/spinner";
import {
    CONFIRM_NOTE,
    CONFIRM_PREVIEW_ERROR,
    CONFIRM_REPLACE_NOTE,
    CONFIRM_TITLE,
    THUMBNAIL_STATUS
} from "../misc/constants";
import { formatFileSize } from "../misc/helpers";

// The browser's own PDF reader needs room for its toolbar and a legible page,
// so the preview gets the dialog's full width rather than a column beside the
// file details.
const FRAME_CLASS = "relative grid min-h-[360px] w-full place-items-center overflow-hidden rounded-lg border border-gray-200 bg-light/60";

const Tile = ({ icon: Icon, message }) => (
    <div className="flex flex-col items-center gap-2 px-2 text-center">
        <span className="grid h-10 w-10 place-items-center rounded-lg border border-gray-200 bg-white text-primary">
            <Icon className="h-5 w-5" />
        </span>
        <p className="text-xs leading-relaxed text-muted">{message}</p>
    </div>
);

Tile.propTypes = {
    icon   : PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
    message: PropTypes.string.isRequired
};

const Detail = ({ label, value }) => (
    <div className="min-w-0">
        <dt className="text-xs uppercase tracking-wide text-muted">{label}</dt>
        <dd className="mt-0.5 break-words text-sm text-gray-700">{value}</dd>
    </div>
);

Detail.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.node
};

// The step between the upload and the save call. By the time this opens the
// file is already in storage — nothing has been written to the database, and
// closing the dialog leaves the contract on record exactly as it was.
//
// Saving it is not the same act as sending it to publishers, which is a
// separate, explicit action behind this.
const ContractConfirmModal = ({
    open,
    staged,
    thumbnail,
    targetName = "",
    isReplacement,
    saving,
    onConfirm,
    onCancel
}) => (
    <Modal
        open={open}
        title={CONFIRM_TITLE}
        subtitle={targetName ? `For ${targetName}. It is saved as not sent.` : "It is saved as not sent."}
        onClose={saving ? () => {} : onCancel}
        width="lg"
        footer={(
            <>
                <LoadingButton
                    color="neutral"
                    size="md"
                    text="Cancel"
                    onClick={onCancel}
                    disabled={saving}
                    testId="cancel-save-contract" />
                <LoadingButton
                    color="dark"
                    size="md"
                    text="Save contract"
                    loadingText="Saving…"
                    loading={saving}
                    onClick={onConfirm}
                    testId="confirm-save-contract" />
            </>
        )}>
        {staged && (
            <div className="flex flex-col gap-5">
                <div className={FRAME_CLASS}>
                    {/* No `sandbox`: Chrome and Firefox run their PDF viewer
                        as a plugin document, which a sandboxed frame is not
                        allowed to instantiate. */}
                    {thumbnail.previewUrl && (
                        <iframe
                            src={thumbnail.previewUrl}
                            title={`Preview of ${staged.name}`}
                            onLoad={thumbnail.onReady}
                            className="h-[360px] w-full bg-white"
                            data-cy="contract-thumbnail" />
                    )}

                    {thumbnail.status === THUMBNAIL_STATUS.LOADING && (
                        <div className="absolute inset-0 grid place-items-center bg-light/60">
                            <ComponentSpinner />
                        </div>
                    )}

                    {thumbnail.status === THUMBNAIL_STATUS.ERROR && (
                        <div className="absolute inset-0 grid place-items-center bg-light">
                            <Tile icon={AlertTriangle} message={CONFIRM_PREVIEW_ERROR} />
                        </div>
                    )}
                </div>

                <div className="min-w-0">
                    <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                        <Detail label="File name" value={staged.originalName || staged.name} />
                        <Detail label="File size" value={formatFileSize(staged.size)} />
                    </dl>

                    <a
                        href={staged.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-1.5 text-sm text-gray-700 hover:text-primary hover:underline">
                        Open the uploaded copy
                        <ExternalLink className="h-3.5 w-3.5" />
                    </a>

                    <p className="mt-4 rounded-lg border border-gray-200 bg-light/40 p-3 text-xs leading-relaxed text-muted">
                        {isReplacement ? CONFIRM_REPLACE_NOTE : CONFIRM_NOTE}
                    </p>
                </div>
            </div>
        )}
    </Modal>
);

ContractConfirmModal.propTypes = {
    open  : PropTypes.bool.isRequired,
    staged: PropTypes.shape({
        name        : PropTypes.string,
        originalName: PropTypes.string,
        size        : PropTypes.number,
        url         : PropTypes.string
    }),
    thumbnail: PropTypes.shape({
        previewUrl: PropTypes.string,
        status    : PropTypes.oneOf(Object.values(THUMBNAIL_STATUS)),
        onReady   : PropTypes.func
    }).isRequired,
    targetName   : PropTypes.string,
    isReplacement: PropTypes.bool,
    saving       : PropTypes.bool,
    onConfirm    : PropTypes.func.isRequired,
    onCancel     : PropTypes.func.isRequired
};

export default ContractConfirmModal;
